import React, { useState, useEffect, useContext } from "react";
import { format, startOfMonth, endOfMonth, addMonths, isSameDay, eachDayOfInterval, getDay, startOfWeek, endOfWeek } from "date-fns";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import { LocationContext } from "./LocationContext";
import { UMSSBlueDark, UMSSBlueLight, UMSSBlue } from "../utils/colors";

function Dates({ onSelect }) {
  const { selectedLocation, selectedServiceType } = useContext(LocationContext); // get selected location + service type from context

  const today = new Date();

  // State
  const [selectedDate, setSelectedDate] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);  // Changed from weekOffset to monthOffset
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(true);

  const db = getFirestore();

  // Get the current month based on offset
  const currentMonth = addMonths(today, monthOffset);
  const monthStart = startOfMonth(currentMonth);
  
  // Get all days in the month and padding days to complete the calendar grid
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });
  
  // Create an array of all days to display (including padding days from prev/next months)
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  // Fetch available slots from Firestore
  useEffect(() => {
    if (!selectedLocation) {
      return;
    }

    setLoading(true);

    // days collection is the source of truth for serviceType; appointment slots
    // don't carry that field, so we cross-reference the two collections.
    const daysQuery = query(
      collection(db, "days"),
      where("officeId", "==", selectedLocation.id)
    );

    const slotsQuery = query(
      collection(db, "offices", selectedLocation.id, "appointments"),
      where("booked", "==", false),
      where("canceled", "==", false)
    );

    Promise.all([getDocs(daysQuery), getDocs(slotsQuery)])
      .then(([daysSnap, slotsSnap]) => {
        // Build a set of date keys for days that match the selected service type
        const validDateKeys = new Set();
        daysSnap.forEach((doc) => {
          const data = doc.data();
          if (selectedServiceType && data.serviceType !== selectedServiceType) return;
          // Doc ID format: M-D-YY-officeId-startTime
          const parts = doc.id.split("-");
          if (parts.length < 3) return;
          const month = parseInt(parts[0], 10) - 1;
          const day   = parseInt(parts[1], 10);
          const year  = 2000 + parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          validDateKeys.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
        });

        // Keep only slots whose date falls on a valid service-type day
        const dates = [];
        slotsSnap.forEach((doc) => {
          const data = doc.data();
          if (!data.dateTime) return;
          const slotDate = data.dateTime.toDate();
          const key = `${slotDate.getFullYear()}-${slotDate.getMonth()}-${slotDate.getDate()}`;
          if (validDateKeys.has(key)) dates.push(slotDate);
        });

        setAvailableDates(dates);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedLocation, selectedServiceType, db]);

  // Handle month navigation
  const handlePrevMonth = () => {
    if (monthOffset > 0) { // Only allow going back if we're not on the current month
      setMonthOffset(monthOffset - 1);
    }
  };

  const handleNextMonth = () => {
    if (monthOffset < 2) {  // Limit to 2 months in the future
      setMonthOffset(monthOffset + 1);
    }
  };

  // Create an array of weekday headers
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div style={{ 
      width: "fit-content", 
      margin: "0 auto", 
      padding: "20px", 
      backgroundColor: "#f9f9f9",
      fontFamily: "Quicksand, sans-serif"
    }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
        <h3 style={{ margin: 0, marginRight: "auto" }}>
          {format(currentMonth, "MMMM yyyy")}
        </h3>
        <button 
          onClick={handlePrevMonth} 
          disabled={monthOffset === 0}
          style={{
            marginRight: "0.5rem",
            padding: "8px 12px",
            border: "none",
            backgroundColor: monthOffset === 0 ? "#ccc" : UMSSBlue,
            color: "white",
            borderRadius: "4px",
            cursor: monthOffset === 0 ? "default" : "pointer"
          }}
        >
          {"<"}
        </button>
        <button 
          onClick={handleNextMonth} 
          disabled={monthOffset === 2}
          style={{
            padding: "8px 12px",
            border: "none",
            backgroundColor: monthOffset === 2 ? "#ccc" : UMSSBlue,
            color: "white",
            borderRadius: "4px",
            cursor: monthOffset === 2 ? "default" : "pointer"
          }}
        >
          {">"}
        </button>
      </div>

      {loading && <p>Loading available dates...</p>}

      {/* Calendar header with weekday names */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: "0.25rem", 
        marginBottom: "0.5rem",
        textAlign: "center",
        fontWeight: "bold"
      }}>
        {weekDays.map((day, index) => (
          <div key={index} style={{ padding: "5px" }}>{day}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(7, 1fr)", 
        gap: "0.25rem",
        marginBottom: "2rem" 
      }}>
        {calendarDays.map((day, index) => {
          const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
          const isToday = isSameDay(day, today);
          const isAvailable = !isToday && isCurrentMonth && availableDates.some((date) => isSameDay(date, day));
          const isSelected = selectedDate && isSameDay(day, selectedDate);

          return (
            <button 
              key={index} 
              onClick={() => {
                if (isCurrentMonth && isAvailable) {
                  setSelectedDate(day);
                  onSelect && onSelect(day);
                }
              }}
              disabled={!isCurrentMonth || !isAvailable}
              style={{
                padding: "10px 5px",
                backgroundColor: !isCurrentMonth 
                  ? "#f8f8f8" 
                  : !isAvailable 
                  ? "#f0f0f0" 
                  : isSelected 
                  ? UMSSBlue 
                  : "#e0e0e0", 
                border: isToday ? `2px solid ${UMSSBlueLight}` : "1px solid transparent",
                borderRadius: "6px",
                fontFamily: "Quicksand, sans-serif",
                cursor: (!isCurrentMonth || !isAvailable) ? "not-allowed" : "pointer",
                color: !isCurrentMonth 
                  ? "#ccc" 
                  : !isAvailable 
                  ? "#e6e6e6" 
                  : isSelected 
                  ? "#fff" 
                  : "#000",
                fontWeight: isSelected ? "bold" : "normal",
                opacity: isCurrentMonth ? 1 : 0.5,
              }}
            >
              <div>{format(day, "d")}</div>
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <h4 style={{ textAlign: "center", margin: "0.5rem 0" }}>
          Selected: {format(selectedDate, "EEEE, MMMM d")}
        </h4>
      )}
    </div>
  );
}

export default Dates;